import {
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect } from "react";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

const TAX_RATE = 0.07;

// ลำดับและป้ายชื่อสถานะ (ให้เรียงตามรูป)
const STATUS_ORDER = ["waiting", "sent", "confirm", "payed"];
const STATUS_LABEL = {
  waiting: "รอขาย",
  sent: "ส่งแล้ว",
  confirm: "ยืนยันการขายแล้ว",
  payed: "ได้รับเงินแล้ว",
};

export default function DailyPurchaseSummary() {
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return formatDate(d);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  const [activityRows, setActivityRows] = useState([]); // {name,count,amount,vat,total}
  const [loading, setLoading] = useState(true);

  const fmt2 = (n) =>
    Number(n || 0).toLocaleString("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const fmtInt = (n) => Number(n || 0).toLocaleString("th-TH");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await services.getSaleTransactions({ startDate, endDate });
        const payload = res?.data?.data ?? res?.data ?? res ?? [];
        console.log('payload', payload)

        if (Array.isArray(payload)) {
          const { activities } = summarizeSales(payload);
          setActivityRows(activities);
        } else {
          setActivityRows(payload.activities || []);
        }
      } catch (e) {
        console.error("fetch error", e);
        setActivityRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  // ปรับให้อยู่ในลำดับตามรูป และเติมสถานะที่ไม่มีข้อมูลให้มีค่า 0
  const orderedActivities = (() => {
    const map = new Map(activityRows.map((r) => [r.name, r]));
    return STATUS_ORDER.map((k) => {
      const r = map.get(k);
      return {
        name: STATUS_LABEL[k],
        count: r?.count || 0,
        amount: r?.amount || 0,
        vat: r?.vat || 0,
        total: r?.total || 0,
      };
    });
  })();

  // แถวสรุป "ทั้งหมด"
  const totalRow = orderedActivities.reduce(
    (acc, r) => {
      acc.count += r.count || 0;
      acc.amount += r.amount || 0;
      acc.vat += r.vat || 0;
      acc.total += r.total || 0;
      return acc;
    },
    { name: "ทั้งหมด", count: 0, amount: 0, vat: 0, total: 0 }
  );

  const cellStyle = { border: "1px solid #e5e7eb" };

  // -------- Export CSV --------
  const exportToCSV = (data, filename = "daily-sales-summary.csv") => {
    if (!data?.length) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((h) => `"${row[h] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const rowsToExport = [
      ...orderedActivities.map((r) => ({
        สถานะของบิล: r.name,
        จำนวนBatch: r.count,
        "ยอดก่อนภาษี": Number(r.amount).toFixed(2),
        "ยอดภาษี": Number(r.vat).toFixed(2),
        "ยอดสุทธิ": Number(r.total).toFixed(2),
      })),
      {
        สถานะของบิล: totalRow.name,
        จำนวนBatch: totalRow.count,
        "ยอดก่อนภาษี": Number(totalRow.amount).toFixed(2),
        "ยอดภาษี": Number(totalRow.vat).toFixed(2),
        "ยอดสุทธิ": Number(totalRow.total).toFixed(2),
      },
    ];
    exportToCSV(rowsToExport);
  };

  return (
    <Box sx={{ p: 2, width: "100%", backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <AppBreadcrumbs />

      {/* ฟิลเตอร์วันที่ */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} my={2}>
        <TextField
          label="เริ่มต้น"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="สิ้นสุด"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography variant="h6">สรุปยอดขายประจำวัน</Typography>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                size="large"
                onClick={handleDownload}
              >
                ข้อมูลหลัก
              </Button>
            </Box>

            <TableContainer component={Paper}>
              <Table
                sx={{
                  width: 1,
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                    <TableCell sx={cellStyle}>สถานะของบิล</TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      จำนวนBatch
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดก่อนภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดภาษี
                    </TableCell>
                    <TableCell align="right" sx={cellStyle}>
                      ยอดสุทธิ
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {orderedActivities.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={cellStyle}>{r.name}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {fmtInt(r.count)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {fmt2(r.amount)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {fmt2(r.vat)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {fmt2(r.total)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* แถวสรุปตามรูป */}
                  <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                    <TableCell sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {totalRow.name}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {fmtInt(totalRow.count)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {fmt2(totalRow.amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {fmt2(totalRow.vat)}
                    </TableCell>
                    <TableCell align="right" sx={{ ...cellStyle, fontWeight: "bold" }}>
                      {fmt2(totalRow.total)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

/* -------- สรุปจากลิสต์รายการขาย (ไม่คิด VAT เมื่อ isVat:false) -------- */
function summarizeSales(transactions = []) {
  const map = new Map(); // key = status

  transactions.forEach((row) => {
    const statusKey = row.status || "waiting";
    const items = Array.isArray(row.items) ? row.items : [];

    // ยอดก่อนภาษี: รวม (netWeight|weight) * pricePerUnit เหมือนเดิม
    const amount = items.reduce(
      (acc, it) =>
        acc +
        Number(it?.netWeight ?? it?.weight ?? 0) * Number(it?.pricePerUnit ?? 0),
      0
    );

    // ถ้า isVat === false => ไม่คิดภาษี (vat = 0)
    // ถ้า isVat === true:
    //   - ถ้ามี row.vat มาแล้ว ใช้ตามนั้น
    //   - ถ้าไม่มี ให้คำนวณตาม TAX_RATE
    let vat = 0;
    if (row?.isVat === true) {
      if (row?.vat != null) {
        vat = Number(row.vat) || 0;
      } else {
        vat = Number((amount * TAX_RATE).toFixed(2));
      }
    }

    const total = amount + vat;

    if (!map.has(statusKey)) {
      map.set(statusKey, { name: statusKey, count: 0, amount: 0, vat: 0, total: 0 });
    }
    const r = map.get(statusKey);
    r.count += 1;
    r.amount += amount;
    r.vat += vat;
    r.total += total;
  });

  // คืน array raw (ยังไม่เรียง/ไม่เปลี่ยนชื่อ)
  const activities = Array.from(map.values());
  return { activities };
}

